// Photo + intro-video carousel used by ExpandedVenueCard and
// MapVenuePreviewSheet. Includes a "parallax on drag" image effect and
// a video slide with a dark loading overlay. Extracted from
// GuestMapSurface.js per FRONTEND_ARCHITECTURE.md.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { animate, motion, useMotionValue, useTransform } from 'framer-motion';

function ParallaxImage({ src, idx, parentX, slideWidth }) {
  const [loaded, setLoaded] = useState(false);
  // Image content shifts slightly opposite to the drag, so the next
  // slide's content appears to come into view a moment before the
  // slide itself does (the App Store "featured" carousel parallax).
  // 0.18 keeps the effect subtle and within the 14 % overscan buffer
  // on each side of the image.
  const imgX = useTransform(parentX, (val) => {
    if (!slideWidth) return 0;
    const offsetFromCenter = val + idx * slideWidth;
    return -offsetFromCenter * 0.18;
  });
  return (
    <div
      style={{
        width: slideWidth || '100%',
        flexShrink: 0,
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        background: 'var(--surface-2)',
      }}
    >
      {!loaded && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(110deg, #ECE6FA 8%, #F7F3FF 38%, #ECE6FA 64%)',
            backgroundSize: '200% 100%',
            animation: 'allure-image-shimmer 1.4s ease-in-out infinite',
          }}
        />
      )}
      <motion.img
        src={src}
        alt=""
        draggable={false}
        onLoad={() => setLoaded(true)}
        style={{
          x: imgX,
          width: slideWidth ? slideWidth * 1.28 : '100%',
          maxWidth: 'none',
          height: '100%',
          objectFit: 'cover',
          marginLeft: slideWidth ? -slideWidth * 0.14 : 0,
          userSelect: 'none',
          pointerEvents: 'none',
          willChange: 'transform',
          opacity: loaded ? 1 : 0,
          transition: 'opacity 320ms ease-out',
        }}
      />
    </div>
  );
}

function VideoSlide({ src, slideWidth, isActive }) {
  const videoRef = useRef(null);
  // `playing` flips to true on the first `playing` event so the
  // loading overlay fades out as soon as actual video frames render.
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return undefined;
    if (isActive) {
      // We deliberately don't seek to 0 first — that seek can force the
      // decoder to flush its just-buffered frames and stall the opening
      // seconds on lower-end Android WebViews.
      const playPromise = video.play();
      if (playPromise?.catch) {
        playPromise.catch(() => { /* autoplay blocked — user can tap to play */ });
      }
    } else {
      try { video.pause(); } catch { /* ignore */ }
      setPlaying(false);
    }
    return undefined;
  }, [isActive, src]);

  return (
    <div
      style={{
        width: slideWidth || '100%',
        flexShrink: 0,
        overflow: 'hidden',
        position: 'relative',
        height: '100%',
        background: '#0F1118',
      }}
    >
      <video
        ref={videoRef}
        src={src}
        muted
        loop
        autoPlay
        playsInline
        preload="auto"
        disablePictureInPicture
        disableRemotePlayback
        data-testid="venue-intro-video-player"
        onPlaying={() => setPlaying(true)}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          display: 'block',
          pointerEvents: 'none',
          userSelect: 'none',
          // Force a dedicated GPU layer so the WebView compositor plays
          // the video on its own texture instead of repainting the
          // surrounding rounded-corner mask every frame.
          transform: 'translateZ(0)',
          willChange: 'transform',
          backfaceVisibility: 'hidden',
        }}
      />
      {/* Dark loading overlay: covers buffering indicator with a clean
          spinner while the video fetches its first frames. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: '#0F1118',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          opacity: playing ? 0 : 1,
          transition: 'opacity 380ms ease',
          pointerEvents: 'none',
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
          style={{
            width: 30,
            height: 30,
            borderRadius: '50%',
            border: '2.5px solid rgba(255,255,255,0.14)',
            borderTopColor: 'rgba(255,255,255,0.85)',
            boxSizing: 'border-box',
          }}
        />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: [0.4, 0.9, 0.4] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            fontFamily: "'Inria Serif', serif",
            fontSize: 12,
            color: '#F5F5F7',
            margin: 0,
            letterSpacing: '0.04em',
          }}
        >
          Loading intro…
        </motion.p>
      </div>
      {/* Subtle gradient so it reads as an intro reel, not a still photo. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, rgba(0,0,0,0) 60%, rgba(8, 6, 22, 0.42) 100%)',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}

export function ImageCarousel({ images, height, videoUrl }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);
  const x = useMotionValue(0);

  const slides = useMemo(() => {
    const items = images.map((src) => ({ type: 'image', src }));
    if (videoUrl) {
      const resolved = videoUrl.startsWith('http') ? videoUrl : `${process.env.REACT_APP_BACKEND_URL}${videoUrl}`;
      return [{ type: 'video', src: resolved }, ...items];
    }
    return items;
  }, [images, videoUrl]);

  useEffect(() => {
    const measure = () => {
      if (containerRef.current) {
        const w = containerRef.current.offsetWidth;
        setWidth(w);
        x.set(-currentIdx * w);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (width > 0) {
      animate(x, -currentIdx * width, { type: 'spring', stiffness: 320, damping: 36, mass: 0.8 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIdx, width]);

  const handleDragEnd = (_, info) => {
    if (width === 0) return;
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    let target = currentIdx;
    if (offset < -width / 4 || velocity < -480) target = currentIdx + 1;
    else if (offset > width / 4 || velocity > 480) target = currentIdx - 1;
    target = Math.max(0, Math.min(slides.length - 1, target));
    setCurrentIdx(target);
    animate(x, -target * width, { type: 'spring', stiffness: 320, damping: 36, mass: 0.8 });
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height, overflow: 'hidden' }}>
      <motion.div
        drag="x"
        dragConstraints={{ left: -(slides.length - 1) * width, right: 0 }}
        dragElastic={0.18}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
        style={{ display: 'flex', height: '100%', x, willChange: 'transform', cursor: 'grab', touchAction: 'pan-y' }}
        whileTap={{ cursor: 'grabbing' }}
      >
        {slides.map((slide, i) => (
          slide.type === 'video'
            ? <VideoSlide key={`video-${slide.src}`} src={slide.src} slideWidth={width} isActive={currentIdx === i} />
            : <ParallaxImage key={`${slide.src}-${i}`} src={slide.src} idx={i} parentX={x} slideWidth={width} />
        ))}
      </motion.div>
      <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6, pointerEvents: 'none' }}>
        {slides.map((slide, index) => (
          <div key={`${slide.type}-${slide.src}-${index}`} style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: index === currentIdx ? '#fff' : 'rgba(255,255,255,0.5)', transition: 'background-color 200ms' }} />
        ))}
      </div>
    </div>
  );
}
